import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { dailyModules, PROCESS_ICON_BY_SLUG, PROCESS_ICON_DEFAULT } from "../../static/dashboardModules";
import { route } from "../../static/route";
import { t } from "../../locales";
import { localizedProcessName } from "../../lib/utils";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { Button } from "../ui/button";
import { useCanAccess } from "../../hooks/useCanAccess";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
} from "../../features/processDefinitions/processDefinitionsSlice";

type RenderModule = {
  key: string;
  icon: string;
  title: string;
  description: string;
  buttonLabel: string;
  secondaryButtonLabel?: string;
  href: string;
  secondaryHref?: string;
  permission: string;
};

const DailyUsageModules = () => {
  const canAccess = useCanAccess();
  const dispatch = useAppDispatch();
  const definitions = useAppSelector(getProcessDefinitions);

  useEffect(() => {
    if (definitions.length === 0) {
      dispatch(fetchProcessDefinitions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const staticModules: RenderModule[] = dailyModules.map((m) => {
    const labels = (t.dashboard as Record<string, any>)[m.key] as {
      title: string;
      description: string;
      buttonLabel: string;
      secondaryButtonLabel?: string;
    };
    return { ...m, ...labels };
  });

  // Process tiles are generated from process_definitions at runtime — adding
  // a new process needs a DB row, not a frontend change.
  const processModules: RenderModule[] = useMemo(() => {
    return [...definitions]
      .filter((d) => d.is_active)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((d): RenderModule => {
        const name = localizedProcessName(d);
        return {
          key: `process-${d.slug}`,
          icon: PROCESS_ICON_BY_SLUG[d.slug] || PROCESS_ICON_DEFAULT,
          title: name,
          description: t.dashboard.processModuleDescription.replace("{name}", name),
          // Primary button always matches what its href actually opens — a
          // has_order:false process (e.g. VMC) shows Receive as primary,
          // not a mislabeled Order button.
          buttonLabel: d.has_order ? t.dashboard.processOrderButtonLabel : t.dashboard.processReceiveButtonLabel,
          secondaryButtonLabel: d.has_order && d.has_receive ? t.dashboard.processReceiveButtonLabel : undefined,
          href: d.has_order ? `${route.processOrders}?process=${d.slug}` : `${route.processReceives}?process=${d.slug}`,
          secondaryHref: d.has_order && d.has_receive ? `${route.processReceives}?process=${d.slug}` : undefined,
          permission: d.has_order ? "process-orders.menu" : "process-receives.menu",
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions]);

  const visibleModules = [...staticModules, ...processModules].filter((m) => canAccess(m.permission));

  if (visibleModules.length === 0) return null;

  return (
    <section>
      <h2 className="text-base md:text-lg font-semibold text-[#113F67] mb-4">
        {t.dashboard.dailyUsageTitle}
      </h2>
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-3">
        {visibleModules.map((module) => (
          <Card
            key={module.key}
            className="flex flex-col h-48 sm:h-56 md:h-64 border-2 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 hover:border-[#34699A]"
          >
            <CardHeader className="text-center pb-2 md:pb-4 px-3 md:px-6 pt-4 md:pt-6">
              <span className="text-2xl md:text-4xl mb-1 md:mb-2">{module.icon}</span>
              <CardTitle className="text-lg md:text-xl font-bold text-[#113F67]">
                {module.title}
              </CardTitle>
              <CardDescription className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4 line-clamp-2">
                {module.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center flex-1 flex flex-col justify-end px-3 md:px-6 pb-4 md:pb-6">
              <div className="flex gap-3 mt-auto">
                <Link to={module.href} className="flex-1">
                  <Button variant="default" size="sm" className="w-full text-sm md:text-base">
                    {module.buttonLabel}
                  </Button>
                </Link>
                {module.secondaryHref && module.secondaryButtonLabel && (
                  <Link to={module.secondaryHref} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full text-sm md:text-base">
                      {module.secondaryButtonLabel}
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default DailyUsageModules;
